/**
 * Client entry point — wires the network, the DOM overlay and the Phaser scene
 * together. It holds no game rules: it forwards input up to the server and
 * renders whatever the server says is true.
 */

import Phaser from 'phaser';
import type { GameSnapshot, ServerMessage } from '@scorched/protocol';
import {
  BattleScene,
  MAX_SKY_ABOVE,
  MAX_SKY_TRIM,
  VIEW_HEIGHT,
  VIEW_WIDTH,
} from './scenes/battle.ts';
import { createRoom, NetClient } from './net.ts';
import { Ui } from './ui.ts';
import { must } from './ui/dom.ts';
import { trackPlayfield } from './ui/playfield.ts';

interface AppState {
  net: NetClient | null;
  you: string | null;
  snapshot: GameSnapshot | null;
  roomCode: string | null;
}

const app: AppState = { net: null, you: null, snapshot: null, roomCode: null };

const game = new Phaser.Game({
  type: Phaser.AUTO,
  parent: 'game-root',
  width: VIEW_WIDTH,
  height: VIEW_HEIGHT,
  // The same near-black as the starfield's own base. A camera shake slides the
  // whole scene, and whatever colour shows in the gutter it opens at the edge
  // is a flashing border unless it matches the sky.
  backgroundColor: '#03030c',
  scale: {
    mode: Phaser.Scale.FIT,
    /*
     * NOT Phaser's own centring. `#game-root` is a grid that already centres
     * the canvas, and CENTER_BOTH adds a margin on top of that — the grid then
     * centres the canvas-plus-margin, and the playfield sat off to the right by
     * half a gutter (355px of black on the left of a 1902px window, 109px on
     * the right).
     */
    autoCenter: Phaser.Scale.NO_CENTER,
  },
  render: {
    pixelArt: true,
    antialias: false,
  },
  scene: [BattleScene],
});

/**
 * Fit the world's WIDTH to the stage, and let the sky give.
 *
 * FIT alone letterboxes the world, which put black bars down both sides of the
 * map in almost every desktop window. So the game is resized to the stage's
 * own shape: the whole width of the world (1280, or wider for a bigger room —
 * it comes from the snapshot), and however many rows of it that shape allows,
 * always anchored at the BOTTOM of the world. A short stage loses up to
 * `MAX_SKY_TRIM` rows of sky; a tall one gains up to `MAX_SKY_ABOVE` rows above
 * the world. Past either cap the bars come back, smaller.
 *
 * Called on every stage resize and every snapshot, because a new match can
 * bring a different width. Cheap when nothing changed.
 */
function fitStage(): void {
  const root = must<HTMLElement>('#game-root');
  const { width, height } = root.getBoundingClientRect();
  if (width === 0 || height === 0) return;
  const worldWidth = app.snapshot?.terrain.width ?? VIEW_WIDTH;
  const wanted = Math.round((worldWidth * height) / width);
  const visible = Math.min(
    VIEW_HEIGHT + MAX_SKY_ABOVE,
    Math.max(VIEW_HEIGHT - MAX_SKY_TRIM, wanted),
  );
  const size = game.scale.gameSize;
  if (visible !== size.height || worldWidth !== size.width) {
    game.scale.setGameSize(worldWidth, visible);
  }
  battleScene()?.refitView();
}

game.events.once(Phaser.Core.Events.READY, () => {
  const stage = must<HTMLElement>('#stage');
  trackPlayfield(stage, game.canvas);
  new ResizeObserver(fitStage).observe(must<HTMLElement>('#game-root'));
  fitStage();
});

function battleScene(): BattleScene | null {
  const scene = game.scene.getScene(BattleScene.KEY);
  return scene instanceof BattleScene ? scene : null;
}

const AIM_BROADCAST_INTERVAL_MS = 80;
let pendingAim: { angleDeg: number; power: number; weapon: string } | null = null;
let aimTimer: number | null = null;

/** Send at most one aim frame per interval, always carrying the latest value. */
function queueAimBroadcast(angleDeg: number, power: number, weapon: string): void {
  pendingAim = { angleDeg, power, weapon };
  if (aimTimer !== null) return;
  aimTimer = window.setTimeout(() => {
    aimTimer = null;
    const aim = pendingAim;
    pendingAim = null;
    if (aim !== null) {
      app.net?.send({ t: 'aim', angleDeg: aim.angleDeg, power: aim.power, weapon: aim.weapon });
    }
  }, AIM_BROADCAST_INTERVAL_MS);
}

const ui = new Ui({
  onCreateRoom: (name, visibility) => {
    ui.clearTitleError();
    void createRoom(visibility)
      .then((roomCode) => join(name, roomCode))
      .catch((error: unknown) => {
        // Wherever the click came from — the console or the public rooms card —
        // the refusal belongs on the title screen's own error line.
        ui.show('title');
        ui.showTitleError(error instanceof Error ? error.message : 'Could not create a room');
      });
  },

  onJoinRoom: (name, roomCode) => {
    ui.clearTitleError();
    join(name, roomCode);
  },

  onStart: () => app.net?.send({ t: 'start' }),

  onLeave: () => {
    app.net?.close();
    app.net = null;
    app.snapshot = null;
    ui.show('title');
  },

  // Aim updates are cosmetic — they only let opponents watch the barrel swing.
  // Holding an arrow key generates one per frame, so they are coalesced into a
  // trailing send: opponents see smooth movement, the socket sees ~12 frames a
  // second instead of hundreds.
  onAimChange: (angleDeg, power, weapon) => {
    queueAimBroadcast(angleDeg, power, weapon);
    // Point our own barrel immediately. The network round trip is throttled and
    // the server does not echo our own aim back to us, so waiting for either
    // would leave the turret frozen while the number on the HUD moved.
    if (app.you !== null) battleScene()?.setLiveAim(app.you, angleDeg);
  },

  onFire: () => {
    const snapshot = app.snapshot;
    if (snapshot === null) return;
    const aim = ui.aim;
    app.net?.send({
      t: 'fire',
      turnNumber: snapshot.turnNumber,
      angleDeg: aim.angleDeg,
      power: aim.power,
      weapon: aim.weapon,
    });
  },

  onBuy: (weapon) => app.net?.send({ t: 'buy', weapon, quantity: 1 }),
  onSell: (weapon) => app.net?.send({ t: 'sell', weapon }),
  onShopDone: () => app.net?.send({ t: 'shopDone' }),

  onBackToTitle: () => {
    app.net?.close();
    app.net = null;
    app.snapshot = null;
    ui.show('title');
  },
});

ui.show('title');
ui.setAim(45, 60);

function join(name: string, roomCode: string): void {
  app.net?.close();
  app.roomCode = roomCode;

  const net = new NetClient(roomCode, name, {
    onMessage: handleMessage,
    onStatus: (status) => {
      if (status === 'reconnecting') ui.showToast('Connection lost — reconnecting…');
      if (status === 'open' && app.snapshot !== null) ui.showToast('Reconnected.');
    },
    onProtocolError: (detail) => {
      console.error('Protocol error:', detail);
      ui.showToast(`Protocol error: ${detail}`);
    },
  });

  app.net = net;
  net.connect();
  ui.show('lobby');
}

function handleMessage(message: ServerMessage): void {
  switch (message.t) {
    case 'welcome':
      app.you = message.you;
      app.roomCode = message.roomCode;
      return;

    case 'lobby':
      if (app.snapshot === null) ui.show('lobby');
      ui.renderLobby(
        message.roomCode,
        message.players,
        message.hostId,
        app.you ?? '',
        message.visibility ?? 'private',
      );
      return;

    case 'state':
      applySnapshot(message.snapshot);
      return;

    case 'events': {
      const scene = battleScene();
      if (scene === null) {
        applySnapshot(message.snapshot);
        return;
      }
      // Lock input while the shell is in the air.
      ui.renderHud(message.snapshot, app.you ?? '', false);
      app.snapshot = message.snapshot;
      fitStage();
      void scene.playEvents(message.events, message.snapshot).then(() => {
        /*
         * Only render this turn if it is still the latest one.
         *
         * `playEvents` resolves for every caller, including one whose turn was
         * overtaken in the queue and never drawn — so a late callback would
         * otherwise re-apply its own older snapshot on top of a newer one, and
         * the client would go back a turn and sit there until something else
         * arrived. That is the normal case rather than a rare one once a room
         * has computer players in it, which is why the guard is here and not in
         * the scene: the scene's job is to draw, and reconciling authoritative
         * state is this file's.
         *
         * Identity rather than turn number, deliberately: a new round restarts
         * the count, so "newer" is not "larger".
         *
         * `solo-bot.spec.ts` › "cannot rewind the board" is what holds this
         * line down. It reschedules the socket so the bot's frame lands 200ms
         * into our own animation every run, rather than hoping the wire does
         * it — and with this condition deleted it reports the board going back
         * to the previous turn and staying there.
         */
        if (app.snapshot === message.snapshot) applySnapshot(message.snapshot);
      });
      return;
    }

    case 'aim':
      // An opponent moved their barrel. Cosmetic, but worth drawing: watching
      // the other tank traverse is most of what makes a turn-based game feel
      // like someone is on the other end of it.
      battleScene()?.setLiveAim(message.playerId, message.angleDeg);
      return;

    case 'chat':
      return;

    case 'error':
      // A refusal the lobby asked for is shown there and goes no further. It
      // matters beyond tidiness: `room_full` answering "add a computer player"
      // is not the same event as `room_full` answering "let me in", and the
      // second one throws the player back to the title screen.
      if (ui.claimError(message.message)) return;
      ui.showToast(message.message);
      if (
        message.code === 'room_full' ||
        message.code === 'bad_protocol' ||
        message.code === 'room_not_found'
      ) {
        ui.showTitleError(message.message);
        ui.show('title');
      }
      /*
       * A room that is not there will not become there.
       *
       * The socket stays open after most refusals, because most of them are
       * about one frame and the connection is still good for the next. This one
       * is about the connection itself: there is no room on the other end of
       * it, so leaving it up would leave the reconnect loop politely asking a
       * phantom room to seat us for the rest of the session. Closed here rather
       * than in `NetClient`, which does not know what any code means.
       */
      if (message.code === 'room_not_found') {
        app.net?.close();
        app.net = null;
        app.snapshot = null;
      }
      return;

    case 'pong':
      return;

    default:
      return;
  }
}

function applySnapshot(snapshot: GameSnapshot): void {
  app.snapshot = snapshot;
  fitStage();
  const you = app.you ?? '';
  const scene = battleScene();
  scene?.render(snapshot);

  switch (snapshot.phase) {
    case 'shopping':
      ui.show('shop');
      ui.renderShop(snapshot, you);
      ui.renderHud(snapshot, you, false);
      return;
    case 'gameover':
      ui.show('gameover');
      ui.renderGameOver(snapshot);
      return;
    default: {
      ui.show('battle');
      const active = snapshot.tanks[snapshot.activeTank];
      const canFire =
        snapshot.phase === 'aiming' &&
        active !== undefined &&
        active.id === you &&
        !(scene?.isAnimating ?? false);
      ui.renderHud(snapshot, you, canFire);
    }
  }
}

// Expose a tiny read-only handle so Playwright can assert on authoritative
// state (terrain hash, health, turn number) without scraping pixels.
declare global {
  interface Window {
    __scorched?: {
      snapshot(): GameSnapshot | null;
      you(): string | null;
      roomCode(): string | null;
      /**
       * Turns the scene was handed faster than it could draw them.
       *
       * The board stays correct when this climbs — every frame carries the
       * authoritative snapshot — so nothing a test can read from the game state
       * notices it. What it costs is shots the player never sees, which is a
       * real defect with no other symptom. See `BattleScene.dropped`.
       */
      droppedTurns(): number;
    };
  }
}

window.__scorched = {
  snapshot: () => app.snapshot,
  you: () => app.you,
  roomCode: () => app.roomCode,
  droppedTurns: () => battleScene()?.droppedTurns ?? 0,
};
